/**
 * Default Plan dnia (timeframes + activities) seeded when a new group is created.
 * Used by Home.jsx and Groups.jsx after group insert.
 */
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

/** Default time slots (Przedziały czasowe) */
export const DEFAULT_TIMEFRAMES = [
  { name: 'Poranek', start_time: '08:00', end_time: '10:00', sequence_order: 0 },
  { name: 'Południe', start_time: '10:00', end_time: '12:00', sequence_order: 1 },
  { name: 'Popołudnie', start_time: '12:00', end_time: '16:00', sequence_order: 2 }
];

/** Default activities (Aktywności) with colors */
const ACTIVITY_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#64748b', '#78716c'
];

export const DEFAULT_ACTIVITIES = [
  'Taniec',
  'Śpiewanie',
  'Wspólne czytanie',
  'Seans przed ekranem',
  'Próby / przedstawienia',
  'Spacer',
  'Plac zabaw',
  'Odpoczynek',
  'Zabawa dowolna',
  'Zajęcia sportowe',
  'Zajęcia plastyczne',
  'Zajęcia tematyczne'
].map((name, index) => ({ name, color: ACTIVITY_COLORS[index] ?? '#6366f1' }));

/**
 * Insert default timeframes and activities for a newly created group.
 * @param {string} groupId - UUID of the group
 * @returns {Promise<void>}
 */
export async function seedDefaultDailyPlanForGroup(groupId) {
  if (!groupId) return;

  try {
    const timeframesToInsert = DEFAULT_TIMEFRAMES.map((tf) => ({
      group_id: groupId,
      name: tf.name,
      start_time: tf.start_time,
      end_time: tf.end_time,
      sequence_order: tf.sequence_order,
      is_active: true
    }));

    const { error: timeframesError } = await supabase
      .from('daily_plan_timeframes')
      .insert(timeframesToInsert);

    if (timeframesError) {
      logger.error('Seed default timeframes error', { groupId, error: timeframesError });
      throw timeframesError;
    }

    const activitiesToInsert = DEFAULT_ACTIVITIES.map((a) => ({
      group_id: groupId,
      name: a.name,
      description: null,
      color: a.color,
      is_active: true
    }));

    const { error: activitiesError } = await supabase
      .from('daily_plan_activities')
      .insert(activitiesToInsert);

    if (activitiesError) {
      logger.error('Seed default activities error', { groupId, error: activitiesError });
      throw activitiesError;
    }

    logger.debug('Default plan dnia seeded for group', { groupId });
  } catch (err) {
    logger.error('seedDefaultDailyPlanForGroup failed', { groupId, err });
    throw err;
  }
}
